import Checkbox from '@shared/components/Checkbox/Checkbox';
import Divider from '@shared/components/Divider/Divider';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import Collapsible from 'modules/Collapsible/Collapsible';
import CollapsibleContent from 'modules/Collapsible/CollapsibleContent';
import CollapsibleHead from 'modules/Collapsible/CollapsibleHead/CollapsibleHead';
import { PermissionCategory, WorkspacePowerLevels } from 'modules/CreateWorkspaceModal/types';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import PermissionCheckbox from 'shared/components/PermissionCheckbox/PermissionCheckbox';
import { permissionDescriptions } from 'shared/components/PermissionCheckbox/permissionDescriptions';
import PermissionColumnsList from 'shared/components/PermissionColumnsList/PermissionColumnsList';
import { PermissionId } from 'utils/powerLevelsConfig';

import usePermissionField from './usePermissionField';
import useScopeLockedPermissions from './useScopeLockedPermissions';
import { getPermissionCategories, getRoleEditorActorPowerLevels } from '../utils';

interface PermissionCategoriesProps {
  className?: string;
  isEditOrCreate?: boolean;
}

type PermissionEntry = PermissionCategory['permissions'][number];

const SHOW_ALL_PERMISSIONS: WorkspacePowerLevels = { workspace: Infinity, chat: Infinity, meeting: Infinity };

const PermissionCategories = ({ className, isEditOrCreate = false }: PermissionCategoriesProps) => {
  const { t } = useTranslation();
  const { workspaceId } = useWorkspacesContext({ requireWorkspaceId: true });
  const actorPowerLevels = useScopedUserPowerLevels(workspaceId);

  const effectiveActorPowerLevels = isEditOrCreate
    ? getRoleEditorActorPowerLevels(actorPowerLevels)
    : SHOW_ALL_PERMISSIONS;

  const {
    permissions: formPermissions,
    togglePermission,
    toggleAllCategoryPermissions,
    isCategoryFullySelected,
    isCategoryPartiallySelected,
  } = usePermissionField({ actorPowerLevels: effectiveActorPowerLevels, exclusiveWorkspaceCap: isEditOrCreate });

  const lockedPermissions = useScopeLockedPermissions(formPermissions);
  const permissionCategories = getPermissionCategories({
    actorPowerLevels: effectiveActorPowerLevels,
    exclusiveWorkspaceCap: isEditOrCreate,
  });

  return (
    <div className="pb-2">
      {permissionCategories.map((category) => {
        const permissionsCount = category.permissions.length;
        const enabledCount = category.permissions.filter((entry) => formPermissions[entry.id as PermissionId]).length;

        return (
          <Collapsible key={category.id} isCollapsible>
            <div className={cx('bg-gray-10 rounded-lg', className)}>
              <CollapsibleHead>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={isCategoryFullySelected(category.id)}
                      partiallySelected={isCategoryPartiallySelected(category.id)}
                      onChange={() => toggleAllCategoryPermissions(category.id)}
                      onClick={(e) => e.stopPropagation()}
                      size="sm"
                    >
                      <span className="font-medium text-size-sm">{t(category.labelKey)}</span>
                    </Checkbox>
                  </div>
                  <span className="text-sm">
                    (
                    {t('workspace_roles.permissions_included', {
                      enabled: enabledCount,
                      total: permissionsCount,
                    })}
                    )
                  </span>
                </div>
                <Collapsible.Toggle hasText={false} arrowIconClassName="w-6 h-6" />
              </CollapsibleHead>
              <CollapsibleContent hasDivider={false} className="!p-0" containerClassName="!space-y-0">
                <div className="px-3">
                  <Divider />
                </div>
                <div className="px-4 pb-4 pt-4">
                  <PermissionColumnsList<PermissionEntry>
                    items={category.permissions}
                    getKey={(entry) => entry.id}
                    renderItem={(entry) => {
                      const permissionId = entry.id as PermissionId;

                      return (
                        <PermissionCheckbox
                          permission={{
                            id: permissionId,
                            label: t(entry.labelKey),
                            enabled: !!formPermissions[permissionId],
                          }}
                          onChange={() => togglePermission(permissionId)}
                          size="sm"
                          description={permissionDescriptions[permissionId]}
                          isLockedByHigherLevel={lockedPermissions[permissionId]}
                        />
                      );
                    }}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
};

export default PermissionCategories;
